'use client';

import React from 'react';

import { SimpleGrid, SimpleGridProps } from '../simple-grid';
import { Box } from '../box';

export interface MetricGridProps extends Omit<SimpleGridProps, 'columns'> {
  /** Number of columns for different breakpoints */
  columns?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  /** Compact mode with smaller gaps */
  compact?: boolean;
  /** Whether to add dividers between items */
  withDividers?: boolean;
}

/**
 * MetricGrid component - responsive grid layout for dashboard metrics
 * Automatically adjusts columns based on screen size
 */
export const MetricGrid: React.FC<MetricGridProps> = ({
  columns = {
    base: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 4,
  },
  compact = false,
  withDividers = false,
  gap = compact ? 3 : 5,
  children,
  ...gridProps
}) => {
  // Convert columns object to responsive array format
  const responsiveColumns = [
    columns.base || 1,
    columns.sm || columns.base || 1,
    columns.md || columns.sm || 2,
    columns.lg || columns.md || 3,
    columns.xl || columns.lg || 4,
  ];

  if (withDividers) {
    return (
      <Box>
        <SimpleGrid
          columns={{ base: responsiveColumns[0], md: responsiveColumns[1] || responsiveColumns[0] }}
          gap={0}
          {...gridProps}
        >
          {React.Children.map(children, (child, index) => (
            <Box
              key={index}
              padding={gap}
              borderRight={
                index % responsiveColumns[0] !== responsiveColumns[0] - 1 ? '1px solid' : undefined
              }
              borderBottom="1px solid"
              borderColor="border.muted"
              _last={{
                borderBottom: 'none',
              }}
            >
              {child}
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <SimpleGrid
      columns={{ base: responsiveColumns[0], md: responsiveColumns[1] || responsiveColumns[0] }}
      gap={gap}
      {...gridProps}
    >
      {children}
    </SimpleGrid>
  );
};

export default MetricGrid;
